/**
 * Single source of truth for which routes expose a clean Markdown (`.md`)
 * sibling, plus helpers for building the URLs that advertise them to LLMs.
 *
 * Kept free of node/fs and dataset imports (only the tiny, static category
 * label maps) so it is safe to bundle on the client. Shared across:
 *   - components/PageActions.vue          (in-page "Copy/View as Markdown" UI + head link)
 *   - server/middleware/crawler.ts        (runtime `Link:` alternate header)
 *   - nuxt.config.ts                       (build-time routeRules `Link:` headers)
 *
 * Markdown siblings come from three sources (all emitted at build time):
 *   - Marketing pages wrapping content in `<div id="exportable-content">`.
 *   - Blog posts sourced from `content/blog/<slug>.md`.
 *   - Catalog pages (home, integrations directory, category pages, integration
 *     detail) generated from the dataset — see server/utils/catalog-md.ts.
 * Must stay in sync with the `prerender:generate` hook in nuxt.config.ts.
 */
import { MAP_CATS, META_CATEGORIES } from './categories';

export const SITE_URL = 'https://unified.to';

/** Fixed marketing routes that emit a `.md` sibling at build time. */
export const MARKDOWN_ROUTES: readonly string[] = [
    '/about',
    '/agents',
    '/embeddedauth',
    '/gdpr',
    '/gdpr/dpa',
    '/gdpr/subprocessors',
    '/mcp',
    '/pricing',
    '/privacy',
    '/productmanagers',
    '/security',
    '/technology',
    '/tos',
];

/** Category route slugs (e.g. /crm, /erp) that have a generated `.md` mirror. */
const CATEGORY_ROUTE_SLUGS: ReadonlySet<string> = new Set([
    ...Object.keys(MAP_CATS),
    ...Object.keys(META_CATEGORIES),
]);

/** Normalize a path: strip trailing slashes, treat empty as root. */
export function normalizePath(path: string): string {
    return path.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
}

/** True for catalog routes whose Markdown is generated from the dataset. */
function isCatalogRoute(p: string): boolean {
    if (p === '/' || p === '/integrations') return true;
    if (p.startsWith('/integrations/')) {
        const type = p.slice('/integrations/'.length);
        // Sub-pages that are not integration detail (e.g. /integrations/roadmap).
        return type !== 'roadmap' && !type.includes('/');
    }
    return CATEGORY_ROUTE_SLUGS.has(p.slice(1));
}

/** True when the given route has a Markdown sibling. */
export function hasMarkdownSibling(path: string): boolean {
    const p = normalizePath(path);
    return (
        MARKDOWN_ROUTES.includes(p) ||
        /^\/blog\/[^/]+$/.test(p) ||
        isCatalogRoute(p)
    );
}

/** The `.md` path for a route (e.g. `/pricing` -> `/pricing.md`, `/` -> `/index.md`). */
export function mdPathFor(path: string): string {
    const p = normalizePath(path);
    return p === '/' ? '/index.md' : `${p}.md`;
}

/** The absolute `.md` URL for a route on the given site (defaults to unified.to). */
export function mdUrlFor(path: string, siteUrl: string = SITE_URL): string {
    const base = siteUrl.replace(/\/+$/, '');
    return `${base}${mdPathFor(path)}`;
}
